import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import PaletteTypeSelector from "@/components/PaletteTypeSelector";
import NumpadTactile from "@/components/NumpadTactile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { batimentColor } from "@/lib/helpers";
import { preparePrint } from "@/lib/printLabel";
import { enqueuePalette, isNetworkError } from "@/lib/offlineQueue";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const MOTIFS = ["Fêlés", "Sales", "Sang", "Cassés", "Calibre non conforme", "Sous-poids", "Autre"];
const BATIMENTS = { P1: "2 FR NCK 01", P2: "2 FR NCK 02", P3: "2 FR NCK 03" };

export default function NouvellePalette() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [tare, setTare] = useState(79);
  const [printSettings, setPrintSettings] = useState({ print_mode: "auto", copies: 1 });
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    type: "",
    batiment: "",
    date_ponte: today,
    categorie: "1",
    motif: "",
    motif_autre: "",
  });
  const [gross, setGross] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setTare(r.data.tare);
      setPrintSettings({ print_mode: r.data.print_mode, copies: r.data.copies });
    });
  }, []);

  const grossNum = parseFloat(gross.replace(",", ".")) || 0;
  const net = grossNum > tare ? +(grossNum - tare).toFixed(1) : 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return !!form.type;
    if (step === 2) return !!form.batiment;
    if (step === 3) return form.type === "P" ? true : !!form.motif;
    if (step === 4) return grossNum > tare;
    return true;
  };

  const submit = async () => {
    if (grossNum <= tare) {
      toast.error("Le poids brut doit être supérieur à la tare.");
      return;
    }
    if (grossNum < 100 || grossNum > 800) {
      if (!window.confirm(`Poids brut ${grossNum} kg hors plage habituelle (100–800 kg). Confirmer ?`))
        return;
    }
    const daysDiff = (new Date(today) - new Date(form.date_ponte)) / 86400000;
    if (daysDiff > 7) {
      if (!window.confirm("La date de ponte remonte à plus de 7 jours. Confirmer ?")) return;
    }
    const payload = {
      type: form.type,
      batiment: form.batiment,
      date_ponte: form.date_ponte,
      categorie: form.categorie,
      motif: form.type === "DECLASSEE" ? form.motif : null,
      motif_autre: form.motif === "Autre" ? form.motif_autre : null,
      poids_brut: grossNum,
      tare,
    };
    const saveOffline = () => {
      enqueuePalette(payload);
      toast.warning("Hors-ligne : palette enregistrée sur la tablette, synchronisation automatique au retour du réseau.");
      navigate("/production");
    };
    if (!navigator.onLine) return saveOffline();
    const job = preparePrint(printSettings);
    try {
      const res = await api.post("/palettes", payload);
      job.print(res.data.pdf_url);
      toast.success(`Palette ${res.data.code_qr} enregistrée — étiquette envoyée à l'impression`);
      navigate(`/palette/${res.data.id}/etiquette`);
    } catch (err) {
      job.cancel();
      if (isNetworkError(err)) return saveOffline();
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const steps = ["Type", "Bâtiment", "Détails", "Pesée", "Validation"];

  return (
    <div className="max-w-2xl mx-auto" data-testid="nouvelle-palette">
      {/* Progress */}
      <div className="flex items-center gap-1 mb-6">
        {steps.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-2 rounded-full ${i + 1 <= step ? "bg-emerald-600" : "bg-slate-200"}`} />
            <div className={`text-[10px] uppercase tracking-wide font-bold mt-1 ${i + 1 === step ? "text-slate-900" : "text-slate-400"}`}>
              {s}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 sm:p-6 min-h-[420px]">
        {step === 1 && (
          <>
            <StepTitle n="1" title="Type de palette" sub="Choix de sécurité critique" />
            <PaletteTypeSelector value={form.type} onChange={(v) => set("type", v)} />
          </>
        )}

        {step === 2 && (
          <>
            <StepTitle n="2" title="Bâtiment d'origine" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(BATIMENTS).map(([b, code]) => (
                <button
                  key={b}
                  data-testid={`step-batiment-${b.toLowerCase()}-btn`}
                  onClick={() => set("batiment", b)}
                  className={`min-h-[120px] rounded-2xl border-4 flex flex-col items-center justify-center transition-all active:scale-[0.98] ${
                    form.batiment === b ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <span className={`px-4 py-1 rounded-lg font-heading font-black text-2xl ${batimentColor(b)}`}>{b}</span>
                  <span className="text-xs text-slate-500 font-mono mt-2">{code}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <StepTitle n="3" title="Date de ponte & Détails" />
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Date de ponte</Label>
                <Input
                  data-testid="step-date-ponte"
                  type="date"
                  max={today}
                  value={form.date_ponte}
                  onChange={(e) => set("date_ponte", e.target.value)}
                  className="mt-1 h-12 text-lg"
                />
              </div>

              {form.type === "P" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Catégorie</Label>
                  <Input
                    data-testid="step-categorie"
                    value={form.categorie}
                    onChange={(e) => set("categorie", e.target.value)}
                    className="mt-1 h-12 text-lg"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-bold text-rose-600">Motif de déclassement (obligatoire)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="step-motif-select">
                    {MOTIFS.map((m) => (
                      <button
                        key={m}
                        data-testid={`motif-${m}`}
                        onClick={() => set("motif", m)}
                        className={`min-h-[56px] rounded-xl border-2 font-semibold text-sm px-2 transition-colors ${
                          form.motif === m ? "bg-rose-600 border-rose-700 text-white" : "border-slate-200 hover:border-rose-400"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  {form.motif === "Autre" && (
                    <Input
                      data-testid="step-motif-autre"
                      placeholder="Préciser le motif"
                      value={form.motif_autre}
                      onChange={(e) => set("motif_autre", e.target.value)}
                      className="mt-3 h-12"
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <StepTitle n="4" title="Pesée" sub="Saisir le poids brut" />
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <WeightBox testId="numpad-display-gross-weight" label="Poids brut" value={`${grossNum.toFixed(1)}`} big />
              <WeightBox testId="numpad-display-tare-weight" label="Tare" value={tare.toFixed(1)} />
              <WeightBox testId="numpad-display-net-weight" label="Poids net" value={net.toFixed(1)} tone="emerald" big />
            </div>
            <NumpadTactile value={gross} onChange={setGross} />
          </>
        )}

        {step === 5 && (
          <>
            <StepTitle n="5" title="Validation" sub="Vérifiez avant enregistrement" />
            <div className={`rounded-xl border-4 p-5 space-y-2 ${form.type === "P" ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50"}`}>
              <Summary label="Type" value={form.type === "P" ? "Conforme P (LIOT SAS)" : "Déclassée"} />
              <Summary label="Bâtiment" value={`${form.batiment} — ${BATIMENTS[form.batiment]}`} />
              <Summary label="Date de ponte" value={form.date_ponte.split("-").reverse().join("/")} />
              {form.type === "P" ? (
                <Summary label="Catégorie" value={form.categorie} />
              ) : (
                <Summary label="Motif" value={form.motif === "Autre" ? form.motif_autre || "Autre" : form.motif} />
              )}
              <Summary label="Poids brut" value={`${grossNum.toFixed(1)} kg`} />
              <Summary label="Tare" value={`${tare.toFixed(1)} kg`} />
              <Summary label="Poids net" value={`${net.toFixed(1)} kg`} bold />
            </div>
          </>
        )}
      </div>

      {/* Nav buttons */}
      <div className="flex gap-3 mt-5">
        {step > 1 && (
          <Button
            data-testid="step-prev-btn"
            variant="outline"
            onClick={() => setStep((s) => s - 1)}
            className="h-14 flex-1 text-base font-bold"
          >
            <ChevronLeft className="w-5 h-5 mr-1" /> Retour
          </Button>
        )}
        {step < 5 ? (
          <Button
            data-testid="step-next-btn"
            disabled={!canNext()}
            onClick={() => setStep((s) => s + 1)}
            className="h-14 flex-1 text-base font-bold bg-slate-900 hover:bg-slate-800"
          >
            Continuer <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        ) : (
          <Button
            data-testid="step-submit-palette-btn"
            onClick={submit}
            className="h-14 flex-1 text-base font-bold bg-emerald-600 hover:bg-emerald-700"
          >
            <Check className="w-5 h-5 mr-1" /> Enregistrer & imprimer l'étiquette
          </Button>
        )}
      </div>
    </div>
  );
}

function StepTitle({ n, title, sub }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Étape {n}</div>
      <h2 className="font-heading font-black text-2xl uppercase tracking-tight text-slate-900">{title}</h2>
      {sub && <p className="text-slate-500 font-medium text-sm">{sub}</p>}
    </div>
  );
}

function WeightBox({ label, value, tone, big, testId }) {
  return (
    <div data-testid={testId} className={`rounded-xl border-2 p-3 ${tone === "emerald" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
      <div className={`font-mono font-black ${big ? "text-2xl sm:text-3xl" : "text-xl"} ${tone === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>
        {value}
      </div>
      <div className="text-[10px] text-slate-400 font-bold">kg</div>
    </div>
  );
}

function Summary({ label, value, bold }) {
  return (
    <div className="flex justify-between items-baseline border-b border-white/60 pb-1">
      <span className="text-xs uppercase tracking-wide text-slate-500 font-bold">{label}</span>
      <span className={`text-slate-900 ${bold ? "font-mono font-black text-xl" : "font-bold"}`}>{value}</span>
    </div>
  );
}
